import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiInfo, FiCheckCircle, FiShield, FiThermometer, 
  FiZap, FiHome, FiAlertTriangle, FiTarget, FiX, FiTrendingDown, 
  FiTool, FiSun, FiDollarSign, FiList, FiCheck, FiMaximize, FiMinimize
} = FiIcons;

function WindowGlazing() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Zweifach, dreifach oder vierfach verglaste Fenster 2026: Was lohnt sich wirklich?",
        "alternativeHeadline": "Warum Dreifachverglasung der Standard ist und wann sich 4-fach Glas nicht lohnt",
        "image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Geschäftsführer MK-AUSBAU",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-01-05",
        "dateModified": "2026-03-25",
        "description": "Fensterverglasung im Vergleich: Ug-Werte, Kosten & Amortisation. Erfahren Sie, warum 3-fach Verglasung 2026 Pflicht ist.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/#/blog/fensterverglasung-2026"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Lohnt sich ein Wechsel von 2-fach auf 3-fach Verglasung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, fast immer. Der Ug-Wert verbessert sich von ca. 2,8 (alt) auf 0,5-0,7 W/m²K. Dies spart bei einem Einfamilienhaus oft 300-500 € Heizkosten pro Jahr und eliminiert Kältestrahlung am Fenster."
            }
          },
          {
            "@type": "Question",
            "name": "Ist Vierfachverglasung sinnvoll?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Für 95% der Gebäude: Nein. Vierfachverglasung ist extrem schwer (Belastung der Scharniere), lässt weniger Licht herein (niedriger g-Wert) und die Mehrkosten amortisieren sich gegenüber 3-fach Glas oft erst nach über 40 Jahren."
            }
          },
          {
            "@type": "Question",
            "name": "Kann ich in einem Altbau einfach 3-fach Fenster einbauen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nur mit Vorsicht! Wenn das Fenster dichter und wärmer ist als die ungedämmte Außenwand, kondensiert Feuchtigkeit an der Wand statt am Glas (Schimmelgefahr). Eine Dämmung der Laibung oder ein Lüftungskonzept ist dann Pflicht."
            }
          }
        ]
      }
    ]
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Fensterverglasung 2026: 2-fach, 3-fach oder 4-fach? | Experten-Vergleich"
        description="Fenster kaufen 2026? 🪟 Entscheidungshilfe: Ug-Werte, Kosten & Schimmel-Vermeidung. Warum 3-fach Glas der Sieger ist & 4-fach Glas meist Geldverschwendung."
        keywords="Fensterverglasung 2026, Dreifachverglasung vs Zweifachverglasung, Ug-Wert Tabelle, Fenster austauschen Kosten, g-Wert Bedeutung, Taupunkt Fenster"
        schema={articleSchema}
        canonical="/blog/fensterverglasung-2026"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4">
              <SafeIcon icon={FiZap} />
              <span>Energieeffizienz & Bauphysik</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Zweifach, dreifach oder vierfach verglaste Fenster 2026: Was lohnt sich wirklich?
            </h1>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Aktualisiert: März 2026</span>
            </div>
          </header>

          {/* Intro Video Stream */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl bg-black aspect-video relative">
            <video 
              src="https://canv.wasacon.com/mk-ausbau/_assets/video/188ba5d0976327788737711a7d3d8a74.mp4" 
              className="absolute top-0 left-0 w-full h-full object-cover"
              controls
              playsInline
              preload="metadata"
              poster="https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg"
            >
              Ihr Browser unterstützt dieses Video-Format nicht.
            </video>
          </div>

          <div className="lead text-xl text-gray-600 leading-relaxed mb-12">
            Wenn du gerade über neue Fenster oder einen Fenstertausch nachdenkst, stolperst du zwangsläufig über die Frage: <strong>Reicht eine Zweifachverglasung noch aus – oder ist Dreifach- bzw. sogar Vierfachverglasung der neue Standard?</strong>
          </div>

          <p className="text-gray-700 mb-8">
            In diesem Beitrag fassen wir die wichtigsten Punkte zusammen, ordnen sie ein und geben dir eine klare Entscheidungsgrundlage – ohne Marketing-Blabla, dafür mit Fokus auf Wirtschaftlichkeit, Technik und Praxis.
          </p>

          {/* 1. Ausgangslage */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            1. Ausgangslage: Alte Fenster, neue Anforderungen
          </h2>
          <p className="text-gray-700 mb-6">
            Viele Gebäude haben noch Fenster aus den 70er, 80er oder 90er Jahren. Das zeigt sich oft daran:
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-start"><SafeIcon icon={FiAlertTriangle} className="text-mk-teal-500 mr-2 mt-1 flex-shrink-0" /> Der <strong>Rahmen</strong> ist häufig noch relativ robust und funktionsfähig.</li>
            <li className="flex items-start"><SafeIcon icon={FiAlertTriangle} className="text-mk-teal-500 mr-2 mt-1 flex-shrink-0" /> Die <strong>Verglasung</strong> ist aber „katastrophal“ schlecht im Vergleich zu heutigen Standards.</li>
            <li className="flex items-start"><SafeIcon icon={FiAlertTriangle} className="text-mk-teal-500 mr-2 mt-1 flex-shrink-0" /> <strong>Folge:</strong> Hohe Energieverluste, Zugerscheinungen, niedrige Oberflächentemperaturen und damit potenziell Schimmelrisiko.</li>
          </ul>
          <p className="text-gray-700 font-medium border-l-4 border-mk-teal-500 pl-4 py-2 bg-mk-teal-50 rounded-r">
            Genau hier setzt die entscheidende Frage an: Was mache ich mit solchen Bestandsfenstern – und welche Verglasung ist sinnvoll?
          </p>

          {/* 2. Bestandsfenster */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            2. Bestandsfenster: Glas tauschen statt alles neu?
          </h2>
          <p className="text-gray-700 mb-6">
            Wenn der vorhandene Rahmen noch in Ordnung ist (Scharniere funktionieren, Rahmen ist stabil), dann ist ein kompletter Fenstertausch oft <strong>wirtschaftlich nicht sinnvoll</strong>. Der energetische Unterschied ist zwar da, aber oft nicht gigantisch genug, um die hohen Kosten eines Komplettausbaus zu rechtfertigen.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl border-t-4 border-mk-teal-500 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <SafeIcon icon={FiMaximize} className="mr-2 text-mk-teal-500" />
                Festverglasung (nicht zu öffnen)
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Hier könnt ihr <strong>direkt auf Dreifachverglasung</strong> gehen.
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-green-500 mr-2" /> Gewicht spielt kaum eine Rolle</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-green-500 mr-2" /> Sehr gutes Dämmniveau möglich</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-t-4 border-orange-500 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <SafeIcon icon={FiMinimize} className="mr-2 text-orange-500" />
                Öffnende Fensterflügel
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Alles, was sich öffnen lässt, hat ein <strong>Gewichtsproblem</strong> bei 3-fach Glas.
              </p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center"><SafeIcon icon={FiAlertTriangle} className="text-orange-500 mr-2" /> Beschläge oft zu schwach für 3-fach</li>
                <li className="flex items-center"><SafeIcon icon={FiCheck} className="text-orange-500 mr-2" /> Empfehlung: <strong>Zweifachverglasung</strong> im alten Rahmen</li>
              </ul>
            </div>
          </div>

          {/* 3. Luftdichtheit */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiWind} className="mr-3 text-mk-teal-600" />
            3. Wichtig: Luftdichtheit & Innendämmung
          </h2>
          <p className="text-gray-700 mb-6">
            Nur das Glas zu tauschen reicht nicht, wenn es durch die Ritzen zieht. Undichte Anschlussfugen sind ein Hauptgrund für Zugluft und Schimmel.
          </p>

          <div className="bg-mk-teal-50 p-8 rounded-2xl mb-12">
            <h3 className="text-xl font-bold text-mk-teal-900 mb-4">Die DIY-Maßnahmen für mehr Komfort:</h3>
            <div className="space-y-6">
              <div className="flex">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-mk-teal-200 text-mk-teal-800 flex items-center justify-center font-bold mr-4">1</div>
                <div>
                  <h4 className="font-bold text-gray-900">Fensteranschluss luftdicht abkleben</h4>
                  <p className="text-sm text-gray-700 mt-1">Besorge dir ein Fenster-Abdichtungsband und klebe den Anschluss zwischen Rahmen und Mauerwerk einmal umlaufend luftdicht ab.</p>
                </div>
              </div>
              <div className="flex">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-mk-teal-200 text-mk-teal-800 flex items-center justify-center font-bold mr-4">2</div>
                <div>
                  <h4 className="font-bold text-gray-900">Innenlaibung dämmen</h4>
                  <p className="text-sm text-gray-700 mt-1">Bringe innen rund ums Fenster eine Dämmplatte an (z.B. Wedi oder Holzfaser, ca. 2-3 cm). Das erhöht die Oberflächentemperatur und senkt das Schimmelrisiko massiv.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Dreifachverglasung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            4. Dreifachverglasung: Der sinnvolle Standard
          </h2>
          <p className="text-gray-700 mb-6">
            Für normal beheizte Wohnräume ist die Empfehlung klar: <strong>Dreifachverglasung</strong> ist der Weg zu höchster Wirtschaftlichkeit und Behaglichkeit.
          </p>
          <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm mb-8">
            <h4 className="font-bold text-gray-900 mb-2">Das Ziel: Ug-Wert ≈ 0,5 W/m²K</h4>
            <p className="text-gray-600 mb-0">
              Das sorgt für deutlich weniger Wärmeverlust und – ganz wichtig – hohe Oberflächentemperaturen an der Scheibe. Kein "Kälteabfall" mehr am Fenster!
            </p>
          </div>

          {/* 5. Wann Zweifach? */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            5. Wann macht Zweifachverglasung noch Sinn?
          </h2>
          <p className="text-gray-700 mb-6">
            Eigentlich nur noch in <strong>niedrig beheizten Räumen</strong> (unter 12-14 °C).
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8 pl-4">
            <li>Keller & Abstellräume</li>
            <li>Unbeheizte Nebenräume</li>
            <li>Garagenfenster</li>
          </ul>
          <p className="text-gray-700">
            Hier sind die Energieverluste aufgrund der geringen Temperaturdifferenz so klein, dass sich der Aufpreis für 3-fach Glas nicht rechnet.
          </p>

          {/* 6. Vierfachverglasung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiX} className="mr-3 text-red-500" />
            6. Vierfachverglasung: Hightech – aber lohnt sich das?
          </h2>
          <p className="text-gray-700 mb-6">
            Technisch beeindruckend (Ug oft um 0,3-0,4), wirtschaftlich oft ein Desaster.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="font-bold text-green-800 mb-2 flex items-center"><SafeIcon icon={FiCheck} className="mr-2"/> Vorteile</h4>
              <ul className="text-sm text-green-900 space-y-1">
                <li>Ca. 30-40% besserer Dämmwert als 3-fach</li>
                <li>Extrem hohe Oberflächentemperatur</li>
              </ul>
            </div>
            <div className="bg-red-50 p-6 rounded-xl">
              <h4 className="font-bold text-red-800 mb-2 flex items-center"><SafeIcon icon={FiX} className="mr-2"/> Nachteile</h4>
              <ul className="text-sm text-red-900 space-y-1">
                <li>Gigantische Mehrkosten</li>
                <li>Extremes Gewicht (Scharnier-Verschleiß!)</li>
                <li>Weniger Lichteinfall (g-Wert)</li>
                <li>Lohnt sich fast nie</li>
              </ul>
            </div>
          </div>

          <p className="italic text-gray-600 mb-8">
            „Die Vierfachverglasung ist im Endeffekt rein über Spezialanlösungen geboten. Ansonsten macht diese Vierfachverglasung wirtschaftlich gesehen für euch gar keinen Sinn.“
          </p>

          {/* 7. 2026 Update */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">
            7. Hat sich 2026 etwas Grundlegendes geändert?
          </h2>
          <div className="bg-gray-100 p-6 rounded-xl mb-12">
            <p className="text-xl font-bold text-center text-gray-900 mb-4">Kurz gesagt: NEIN.</p>
            <p className="text-gray-700 text-center">
              Die Kosten für Vierfachverglasung sind nicht signifikant gefallen. Dreifachverglasung bleibt der Sweet-Spot aus Kosten und Nutzen.
            </p>
          </div>

          {/* 8. Fazit */}
          <div className="bg-mk-teal-600 text-white p-10 rounded-[2.5rem] shadow-2xl mt-16 mb-12">
            <h2 className="text-white text-3xl font-bold mb-8 mt-0 text-center">Fazit: Welche Verglasung für dich?</h2>
            
            <div className="space-y-6">
              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="font-bold text-mk-teal-100 mb-2">Bestandsfenster (Rahmen gut)</h3>
                <p className="text-sm text-white">
                  • Festverglasung → <strong>Dreifachverglasung</strong><br/>
                  • Öffnende Flügel → <strong>Zweifachverglasung</strong> (wegen Gewicht)
                </p>
              </div>

              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="font-bold text-mk-teal-100 mb-2">Neue Fenster (Wohnräume)</h3>
                <p className="text-sm text-white">
                  • Standard → <strong>Dreifachverglasung</strong> (Ug ≈ 0,5)<br/>
                  • Bestes Kosten-Nutzen-Verhältnis
                </p>
              </div>

              <div className="bg-white/10 p-4 rounded-xl">
                <h3 className="font-bold text-mk-teal-100 mb-2">Niedrig beheizte Räume ({'<'} 12°C)</h3>
                <p className="text-sm text-white">
                  • Empfehlung → <strong>Zweifachverglasung</strong> reicht völlig aus.
                </p>
              </div>
            </div>

            <p className="text-white/80 text-sm mt-8 text-center italic">
              Nutze diese Leitlinien, um Angebote zu bewerten und Fehlentscheidungen zu vermeiden.
            </p>
          </div>

          <div className="text-center py-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Planen Sie einen Fenstertausch?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir beraten Sie ehrlich zur Wirtschaftlichkeit und führen den Einbau inklusive aller Anschlüsse fachgerecht aus. Keine Experimente beim Fenstertausch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/kontakt" 
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Beratungstermin vereinbaren
              </Link>
              <Link 
                to="/daemmtechnik" 
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zur Dämmtechnik
              </Link>
            </div>
          </div>
        </motion.article>
      </div>
      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default WindowGlazing;